import { isDefinedObject, isDefinedString, reduce } from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable } from "mobx";

import { apiCallGetMyCSREffectivenessData } from "../../../api/csr-api/api-call-get-my-csr-effectiveness-data";
import { apiCallGetMyCSRPerformanceData } from "../../../api/csr-api/api-call-get-my-csr-performance-data";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { et3ReadableDecimalNumber } from "../../../helpers/et3-data-helper";
import et3ReadableDate, {
  isDateRangeValid
} from "../../../helpers/et3-date-helper";
import {
  TCorePage,
  TEffectivenessData,
  TEventsPickerType
} from "../../../Types";
import getCorePersonalStore from "../../core/core-personal-store";
import getCoreUIStore from "../../core/core-ui-store";

class MyCSRPerformanceStore {
  fromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 1,
      daysAdded: 0
    })
  );
  toDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  performanceData = observable.box<
    {
      activeTime: string;
      pageName: TCorePage;
      numberOfEvents: number;
      eventPerHour: number;
    }[]
  >([]);
  effectivenessData = observable.box<TEffectivenessData[]>([]);

  effectivenessDataOrdered = computed(() => {
    return orderBy(
      this.effectivenessData.get().map((data) => ({
        ...data,
        audited_events: data.audited_events.textValue,
        minor_errors: data.minor_errors.textValue,
        major_errors: data.major_errors.textValue
      })),
      ["event_name"],
      ["asc"]
    );
  });

  getPerformanceData = async () => {
    this.effectivenessData.set([]);

    if (
      !isDateRangeValid({
        fromDate: this.fromDate.get(),
        toDate: this.toDate.get(),
        isLimitedToOneMonth: true
      })
    ) {
      return;
    }

    getCoreUIStore().setIsLoading("CSR_MY_PERFORMANCE", true);
    const performanceData = await apiCallGetMyCSRPerformanceData({
      startDate: this.fromDate.get(),
      endDate: this.toDate.get()
    });
    const { data: perfData, status: perfStatus } = performanceData;

    if (perfStatus === "ERROR") {
      return;
    }
    const newPerfData = perfData.map((item) => {
      return {
        ...item,
        activeTime: et3ReadableDate(item.activeTime),
        eventPerHour: et3ReadableDecimalNumber(item.eventPerHour, 2)
      };
    });
    this.performanceData.set(newPerfData);
    await Promise.all([
      this.getAuditCount("approval_picture"),
      this.getAuditCount("approval_description"),
      this.getAuditCount("approval_peer_description"),
      this.getAuditCount("report"),
      this.getAuditCount("investigation_cases")
    ]);
    this.setTotal();
    getCoreUIStore().setIsLoading("CSR_MY_PERFORMANCE", false);
  };
  getAuditCount = async (eventName: TEventsPickerType) => {
    const adminId = reduce(() => getCorePersonalStore().user.get().id, 0);

    const { data, status } = await apiCallGetMyCSREffectivenessData({
      eventName,
      adminId,
      fromDate: this.fromDate.get(),
      toDate: this.toDate.get()
    });
    if (status === "OK" && isDefinedObject(data) && !isDefinedString(data)) {
      const no_issue = reduce(() => data.no_issue_count, 0);
      const minor_errors = reduce(() => data.minor_issue_count, 0);
      const major_errors = reduce(() => data.major_issue_count, 0);
      const total = no_issue + minor_errors + major_errors;
      const total_issues = minor_errors + major_errors;
      const minor_errors_percentage =
        total === 0 ? 0 : (100 * minor_errors) / total;
      const major_errors_percentage =
        total === 0 ? 0 : (100 * major_errors) / total;
      const total_errors_percentage =
        minor_errors_percentage + major_errors_percentage;

      const effectivenessData = this.effectivenessData.get();
      this.effectivenessData.set([
        ...effectivenessData,
        {
          id: `${eventName}`,
          event_name: eventName,
          audited_events: {
            issueType: "all",
            eventName,
            textValue: total
          },
          minor_errors: {
            issueType: "minor",
            eventName,
            textValue: minor_errors
          },
          major_errors: {
            issueType: "major",
            eventName,
            textValue: major_errors
          },
          total_issues,
          minor_errors_percentage: et3ReadableDecimalNumber(
            minor_errors_percentage,
            2
          ),
          major_errors_percentage: et3ReadableDecimalNumber(
            major_errors_percentage,
            2
          ),
          total_errors_percentage: et3ReadableDecimalNumber(
            total_errors_percentage,
            2
          )
        }
      ]);
    }
  };
  setTotal = () => {
    const effectivenessData = this.effectivenessData.get();
    let audited_events = 0;
    let minor_errors = 0;
    let major_errors = 0;
    let total_issues = 0;
    let minor_errors_percentage = 0;
    let major_errors_percentage = 0;
    let total_errors_percentage = 0;
    effectivenessData.forEach((element) => {
      audited_events = audited_events + element.audited_events.textValue;
      minor_errors = minor_errors + element.minor_errors.textValue;
      major_errors = major_errors + element.major_errors.textValue;
      total_issues = total_issues + element.total_issues;

      minor_errors_percentage =
        audited_events === 0 ? 0 : (100 * minor_errors) / audited_events;
      major_errors_percentage =
        audited_events === 0 ? 0 : (100 * major_errors) / audited_events;
      total_errors_percentage =
        minor_errors_percentage + major_errors_percentage;
    });
    this.effectivenessData.set([
      ...effectivenessData,
      {
        id: "total",
        event_name: "total",
        audited_events: {
          issueType: "all",
          eventName: "total",
          textValue: audited_events
        },
        minor_errors: {
          issueType: "minor",
          eventName: "total",
          textValue: minor_errors
        },
        major_errors: {
          issueType: "major",
          eventName: "total",
          textValue: major_errors
        },
        total_issues,
        minor_errors_percentage: et3ReadableDecimalNumber(
          minor_errors_percentage,
          2
        ),
        major_errors_percentage: et3ReadableDecimalNumber(
          major_errors_percentage,
          2
        ),
        total_errors_percentage: et3ReadableDecimalNumber(
          total_errors_percentage,
          2
        )
      }
    ]);
  };
}

const getMyCSRPerformanceStore = memoize(
  () => new MyCSRPerformanceStore(),
  () => 1
);
export default getMyCSRPerformanceStore;
